<template>
    <div class="combo-box">
        <div class="combo-box-current" :class="{ disabled: !active }" @click="toggleComboBox">
            <v-switch background-color="success" v-model="active" @click.native.stop></v-switch>
            <span class="selected-items">
                <template v-if="selectedItems.length === 0">
                    {{ this.noItemsText }}
                </template>
                <template v-else>
                    <span v-for="(item,index) in selectedItems">
                        <span v-if="index < selectedItems.length-1" class="selected-item" :key="item.value">
                            {{ item.text }},
                        </span>
                        <span v-else class="selected-item" :key="item.value">
                            {{ item.text }}
                        </span>
                    </span>
                </template>
            </span>
            <span class="combo-box-open-sign">
                <i v-if="!open" class="fa fa-plus"></i>
                <i v-else class="fa fa-minus"></i>
            </span>
        </div>
        <div class="combo-box-open" :class="{ open, disabled: !active }">
            <div class="combo-box-tags" v-if="selectedItems.length > 0">
                <v-chip close close-icon="fa-times" v-for="item in selectedItems" :key="item.value"
                        @input="removeSelectedItem(item)" :disabled="!active"
                >
                    {{ item.text}}
                </v-chip>
            </div>
            <div class="combo-box-separator" v-if="showSearch"></div>
            <div class="combo-box-search" :class="{ 'no-clear-icon': !hasSearchText }" v-if="showSearch">
                <v-input append-icon="fa-times" prepend-icon="fa-search" @click:append="clearSearchText">
                    <input type="text" v-model="searchText" :disabled="!active" :placeholder="placeholder" />
                </v-input>
            </div>
            <div class="combo-box-separator"></div>
            <div class="combo-box-items">
                <v-checkbox v-for="item in limitedFilteredItems" :key="item.value"
                    :input-value="isItemSelected(item)" :value="isItemSelected(item)"
                    :disabled="!active"  @change="toggleSelectedItem(item)"
                >
                    <template v-slot:label>
                        <template v-if="isItemSelected(item)">
                            <img src="../../assets/images/rectangle-checked.svg" alt="Check mark" />
                        </template>
                        <template v-else>
                            <img src="../../assets/images/rectangle-unchecked.svg" alt="Check mark" />
                        </template>
                        <span>{{ item.text }}</span>
                    </template>
                </v-checkbox>
            </div>
            <p class="combo-box-hint" v-if="hiddenItemsNumber > 0 && !isCategories">
                + {{hiddenItemsNumber}} more. Please refine your search to get the data you want.
            </p>
            <div class="combo-box-separator" v-if="hasSearchText && selectedItems.length > 0"></div>
            <div class="chart-loading" :class="{ open: loader }">
                <i class="fa fa-spinner fa-spin"></i>
            </div>
        </div>
    </div>
</template>

<script>

const ITEMS_TO_SHOW = 20;

export default {
    props: {
        items: {
            type: Array,
            default: () => [],
        },
        maxItems: {
            type: Number,
            default: ITEMS_TO_SHOW,
        },
        value: {
            type: Array,
        },
        noItemsText: {
            type: String,
        },
        placeholder: {
            type: String,
        },
        showSearch: {
            type: Boolean,
            default: true,
        },
        filters: {
            type: Object,
        },
        open: {
            type: Boolean,
            default: false,
        },
        reset: {
            type: Boolean,
            default: false
        },
        loader: {
            type: Boolean,
            default: false
        },
        isCategories: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            active: true,
            searchText: '',
            selectedValues: [],
        };
    },
    watch: {
        value() {
            this.setDefaultSelectedValues();
        },
        filters() {
            this.searchText = '';
            this.closeComboBox();
        },
        active() {
            this.onActiveChange();
        },
        reset(){
             this.searchText = '';
             this.active = true;
        }
    },
    computed: {
        hasSearchText() {
            return this.searchText !== '';
        },
        filteredItems() {
            if (this.showSearch && !this.hasSearchText && !this.isCategories) {
                return [...this.items].slice(0, ITEMS_TO_SHOW);
            }

            return this.items.filter(item => item.text.toLowerCase().indexOf(this.searchText.toLowerCase()) >= 0);
        },
        limitedFilteredItems() {
            if (this.isCategories) {
                return this.filteredItems.filter((item, index) => index > -1);
            }
            return this.filteredItems.filter((item, index) => index < this.maxItems);
        },
        selectedItems() {
            return this.items.filter(item => this.isItemSelected(item));
        },
        hiddenItemsNumber() {
            if (this.showSearch && !this.hasSearchText) {
                return this.items.length - ITEMS_TO_SHOW;
            }

            return this.filteredItems.length - this.limitedFilteredItems.length;
        },
    },
    created() {
        this.setDefaultSelectedValues();
    },
    methods: {
        toggleComboBox() {
            if (this.active) {
                this.$emit('update:open', !this.open);
            }
        },
        closeComboBox() {
            this.$emit('update:open', false);
        },
        setDefaultSelectedValues() {
            this.selectedValues = this.value ? this.value : [];
        },
        clearSearchText() {
            this.searchText = '';
        },
        isItemSelected(item) {
            return this.selectedValues.indexOf(item.value) >= 0;
        },
        addSelectedItem(item) {
            if (!this.isItemSelected(item)) {
                this.selectedValues.push(item.value);
                this.$emit('input', this.selectedValues);
            }
        },
        removeSelectedItem(item) {
            if (this.isItemSelected(item)) {
                const itemValueIndex = this.selectedValues.indexOf(item.value);
                this.selectedValues.splice(itemValueIndex, 1);

                this.$emit('input', this.selectedValues);
            }
        },
        toggleSelectedItem(item) {
            if (this.isItemSelected(item)) {
                this.removeSelectedItem(item);
            } else {
                this.addSelectedItem(item);
            }
        },
        onActiveChange() {
            if (!this.active) {
                this.closeComboBox();
                this.searchText = '';
            }

            this.$emit('toggleActive', this.active);
        },
    },
};

</script>

<style lang="scss">
    @import '../../style/common/ComboBox.scss';
</style>
